/**
 * Year 12 ICT Curriculum - Complete Data
 * Based on Vanuatu National ICT Curriculum Framework
 * Includes: Strands, SLOs, Yearly Planners, Worksheets, Tests, Resources
 */

export const year12ICTCurriculum = {
  subject: "Information and Communication Technology (ICT)",
  yearLevel: 12,
  year: 2025,
  
  courseOverview: {
    description: "Year 12 ICT develops students' understanding of PC systems, application software, graphics manipulation, and communication networks with emphasis on practical skills and safety.",
    totalWeeks: 40,
    termsPerYear: 4,
    weeksPerTerm: 10,
    periodsPerWeek: 5,
    totalPeriods: 200
  },

  // Main Learning Outcomes
  majorLearningOutcomes: [
    "12ICT1: Demonstrate key understanding of operating a desktop computer, manage computer files and protect files from all forms of corruption",
    "12ICT2: Demonstrate skills and understanding of common user applications (word-processors, spreadsheets, databases) for specific requirements",
    "12ICT3: Demonstrate key understanding of simple DTP documents, process digital images, create presentations and computer graphics",
    "12ICT4: Demonstrate key understanding of networking concepts, create web pages, and communicate and research using the Internet"
  ],

  assessmentWeighting: {
    "Practical Work": 40,
    "Theory Tests": 25,
    "Projects": 25,
    "Participation": 10
  },

  // ============================================
  // STRAND 1: PERSONAL COMPUTER (PC) SYSTEMS
  // ============================================
  strands: [
    {
      id: "strand1",
      code: "12ICT1",
      name: "Strand 1: Personal Computer (PC) Systems",
      description: "Understanding of operating a desktop computer, managing files and protecting from corruption",
      color: "blue",
      majorOutcome: "12ICT1: Upon successful completion of this strand, students are able to demonstrate key understanding of operating a desktop computer, manage computer files and protect files from all forms of corruption.",
      
      subStrands: [
        {
          id: "substrand1.1",
          code: "12ICT1.1",
          name: "Computer Hardware Maintenance",
          keyOutcome: "Demonstrate understanding in installing, testing and maintaining PC hardware",
          slos: [
            // Level 1 - Basic Identification
            { code: "12ICT1.1.1.1", description: "Identify a major hardware component of a PC", level: 1 },
            { code: "12ICT1.1.1.2", description: "Test that the PC can be powered on", level: 1 },
            { code: "12ICT1.1.1.3", description: "Name/Identify an Operating System (OS) for the PC", level: 1 },
            { code: "12ICT1.1.1.4", description: "Install a part of the OS", level: 1 },
            { code: "12ICT1.1.1.5", description: "Test that the OS is installed properly", level: 1 },
            { code: "12ICT1.1.1.6", description: "Identify a PC hardware diagnostic tool", level: 1 },
            { code: "12ICT1.1.1.7", description: "Perform a basic hardware maintenance task", level: 1 },
            { code: "12ICT1.1.1.8", description: "Identify a possible OS issue", level: 1 },
            { code: "12ICT1.1.1.9", description: "Perform a possible OS maintenance scan", level: 1 },
            { code: "12ICT1.1.1.10", description: "Define OS update", level: 1 },
            { code: "12ICT1.1.1.11", description: "State the importance of an OS update", level: 1 },
            { code: "12ICT1.1.1.12", description: "Setup an OS update task", level: 1 },
            { code: "12ICT1.1.1.13", description: "Name/Identify a proper OS security protection", level: 1 },
            { code: "12ICT1.1.1.14", description: "State a key function of an OS security protection", level: 1 },
            { code: "12ICT1.1.1.15", description: "Setup a proper OS security protection", level: 1 },
            { code: "12ICT1.1.1.16", description: "Define PC troubleshooting", level: 1 },
            { code: "12ICT1.1.1.17", description: "State the importance of following proper trouble-shooting approaches", level: 1 },
            { code: "12ICT1.1.1.18", description: "Perform a basic PC trouble-shooting step", level: 1 },
            // Level 2 - Comprehension
            { code: "12ICT1.1.2.1", description: "Identify major hardware components of a PC", level: 2 },
            { code: "12ICT1.1.2.2", description: "Identify common PC ports", level: 2 },
            { code: "12ICT1.1.2.3", description: "Identify common PC cables", level: 2 },
            { code: "12ICT1.1.2.4", description: "Install components of the OS", level: 2 },
            { code: "12ICT1.1.2.5", description: "Install and run a PC hardware diagnostic tool", level: 2 },
            { code: "12ICT1.1.2.6", description: "Assess and report hardware issues after running a diagnostic test", level: 2 },
            { code: "12ICT1.1.2.7", description: "Perform proper hardware maintenance tasks", level: 2 },
            { code: "12ICT1.1.2.8", description: "Identify possible OS issues", level: 2 },
            { code: "12ICT1.1.2.9", description: "Perform possible OS maintenance scans", level: 2 },
            { code: "12ICT1.1.2.10", description: "Outline the process of OS updates", level: 2 },
            { code: "12ICT1.1.2.11", description: "Setup all required OS update tasks", level: 2 },
            { code: "12ICT1.1.2.12", description: "Outline proper OS security protections", level: 2 },
            { code: "12ICT1.1.2.13", description: "Outline/List key functions of an OS security protection", level: 2 },
            { code: "12ICT1.1.2.14", description: "Setup all required OS security protections", level: 2 },
            { code: "12ICT1.1.2.15", description: "Outline the importance of following trouble-shooting approaches", level: 2 },
            { code: "12ICT1.1.2.16", description: "Perform PC trouble-shooting steps", level: 2 },
            // Level 3 - Application
            { code: "12ICT1.1.3.1", description: "Build a PC with major hardware components", level: 3 },
            { code: "12ICT1.1.3.2", description: "Install all required components of an OS successfully", level: 3 },
            { code: "12ICT1.1.3.3", description: "Analyze possible causes of hardware issues", level: 3 },
            { code: "12ICT1.1.3.4", description: "Perform all required hardware maintenance to improve the performance of a working PC", level: 3 },
            { code: "12ICT1.1.3.5", description: "Perform possible OS maintenance scans and resolve OS issues", level: 3 },
            { code: "12ICT1.1.3.6", description: "Explain the importance of OS updates", level: 3 },
            { code: "12ICT1.1.3.7", description: "Explain how OS security protections work", level: 3 },
            { code: "12ICT1.1.3.8", description: "Perform PC trouble-shooting approaches based on a specific PC problem", level: 3 },
            // Level 4 - Analysis/Synthesis
            { code: "12ICT1.1.4.1", description: "Build a working PC with all the required hardware components", level: 4 },
            { code: "12ICT1.1.4.2", description: "Recommend proper fixes for hardware issues based on a completed diagnostic test", level: 4 },
            { code: "12ICT1.1.4.3", description: "Perform all possible hardware maintenance tasks with proper OS safety settings", level: 4 },
            { code: "12ICT1.1.4.4", description: "Discuss the impact of OS security protection on the functioning of a computer", level: 4 },
            { code: "12ICT1.1.4.5", description: "Apply possible solutions to a PC problem after performing proper diagnostic approaches", level: 4 }
          ]
        },
        {
          id: "substrand1.3",
          code: "12ICT1.3",
          name: "Programming II",
          keyOutcome: "Demonstrate understanding in planning, designing and writing code to solve a given problem",
          slos: [
            // Level 1
            { code: "12ICT1.3.1.1", description: "Define structured programming", level: 1 },
            { code: "12ICT1.3.1.2", description: "State a component of structured programming", level: 1 },
            { code: "12ICT1.3.1.3", description: "Define sequence in structured programming", level: 1 },
            { code: "12ICT1.3.1.4", description: "Define repetition in structured programming", level: 1 },
            { code: "12ICT1.3.1.5", description: "Define selection in structured programming", level: 1 },
            { code: "12ICT1.3.1.6", description: "State an extension to structured programming", level: 1 },
            { code: "12ICT1.3.1.7", description: "State an example of structured programming", level: 1 },
            { code: "12ICT1.3.1.8", description: "Define software development methodology", level: 1 },
            { code: "12ICT1.3.1.9", description: "Name a known software development methodology", level: 1 },
            { code: "12ICT1.3.1.10", description: "State a key feature of a known software development methodology", level: 1 },
            { code: "12ICT1.3.1.11", description: "State the importance of following the process of a software development methodology", level: 1 },
            { code: "12ICT1.3.1.12", description: "Present a part of a program design using a basic software development methodology", level: 1 },
            { code: "12ICT1.3.1.13", description: "Name/Identify a programming language to use", level: 1 },
            { code: "12ICT1.3.1.14", description: "Name/Identify a program editor for writing codes", level: 1 },
            { code: "12ICT1.3.1.15", description: "Declare a variable using correct programming syntax", level: 1 },
            { code: "12ICT1.3.1.16", description: "Define/Identify arrays", level: 1 },
            { code: "12ICT1.3.1.17", description: "Declare a single array using correct syntax", level: 1 },
            { code: "12ICT1.3.1.18", description: "Assign an appropriate data type", level: 1 },
            { code: "12ICT1.3.1.19", description: "Create a part of a program function", level: 1 },
            { code: "12ICT1.3.1.20", description: "Create a statement of code as part of a program solution using structured programming concepts", level: 1 },
            { code: "12ICT1.3.1.21", description: "Run the program", level: 1 },
            { code: "12ICT1.3.1.22", description: "Perform a program test", level: 1 },
            // Level 2
            { code: "12ICT1.3.2.1", description: "Outline major components of structured programming", level: 2 },
            { code: "12ICT1.3.2.2", description: "Outline key examples of structured programming", level: 2 },
            { code: "12ICT1.3.2.3", description: "List some of the known software development methodologies", level: 2 },
            { code: "12ICT1.3.2.4", description: "Outline key features of a known software development methodology", level: 2 },
            { code: "12ICT1.3.2.5", description: "Outline the importance of following the process of software development methodologies", level: 2 },
            { code: "12ICT1.3.2.6", description: "Present the major components of a program design using a basic software development methodology", level: 2 },
            { code: "12ICT1.3.2.7", description: "Declare variables correctly as required coding elements", level: 2 },
            { code: "12ICT1.3.2.8", description: "Declare an array with required elements", level: 2 },
            { code: "12ICT1.3.2.9", description: "Assign appropriate data types to meet coding requirements", level: 2 },
            { code: "12ICT1.3.2.10", description: "Apply conditional statements to meet coding requirements", level: 2 },
            { code: "12ICT1.3.2.11", description: "Apply appropriate control structures to meet coding requirements", level: 2 },
            { code: "12ICT1.3.2.12", description: "Create required components of a program function", level: 2 },
            { code: "12ICT1.3.2.13", description: "Produce part of a program code using structured programming concepts", level: 2 },
            { code: "12ICT1.3.2.14", description: "Run the program to present the required program output", level: 2 },
            { code: "12ICT1.3.2.15", description: "Perform proper program debugging using an appropriate tool", level: 2 },
            // Level 3
            { code: "12ICT1.3.3.1", description: "Explain the importance of following the process of software development methodologies", level: 3 },
            { code: "12ICT1.3.3.2", description: "Present a design of a program solution that can be used to solve a problem using a basic software development methodology", level: 3 },
            { code: "12ICT1.3.3.3", description: "Apply required coding elements to a program using correct syntax", level: 3 },
            { code: "12ICT1.3.3.4", description: "Create appropriate functions that can perform a specific task correctly", level: 3 },
            { code: "12ICT1.3.3.5", description: "Present a complete program code using structured programming", level: 3 },
            // Level 4
            { code: "12ICT1.3.4.1", description: "Discuss the impact of following the process of software development methodologies", level: 4 },
            { code: "12ICT1.3.4.2", description: "Present a design of a program solution to meet a specific requirement using a known software development methodology", level: 4 },
            { code: "12ICT1.3.4.3", description: "Apply required coding elements to meet specific program requirements using correct syntax", level: 4 },
            { code: "12ICT1.3.4.4", description: "Present a complete program code that fulfils specific requirements using structured programming", level: 4 }
          ]
        },
        {
          id: "substrand1.4",
          code: "12ICT1.4",
          name: "Keyboard Skills II",
          keyOutcome: "Demonstrate understanding in typing using the keyboard's letter keys, number keys and numeric keypad correctly at a minimum speed",
          slos: [
            { code: "12ICT1.4.1.1", description: "Place a finger correctly on a home key", level: 1 },
            { code: "12ICT1.4.1.2", description: "Identify the position of each letter correctly on the keyboard", level: 1 },
            { code: "12ICT1.4.1.3", description: "Use a correct placement of a finger to type a key of the keyboard", level: 1 },
            { code: "12ICT1.4.1.4", description: "Produce a capital letter by pressing the shift key", level: 1 },
            { code: "12ICT1.4.1.5", description: "Produce capital letters by using the Caps Lock key on the keyboard", level: 1 },
            { code: "12ICT1.4.1.6", description: "Type a given piece of information", level: 1 },
            { code: "12ICT1.4.1.7", description: "Use a correct finger to type numbers", level: 1 },
            { code: "12ICT1.4.1.8", description: "Type a required number", level: 1 },
            { code: "12ICT1.4.2.1", description: "Place fingers correctly on home keys", level: 2 },
            { code: "12ICT1.4.2.2", description: "Identify position of several letters correctly on the keyboard", level: 2 },
            { code: "12ICT1.4.2.3", description: "Use correct placement of fingers on some keys of the keyboard", level: 2 },
            { code: "12ICT1.4.2.4", description: "Type a passage of information at a rate of 30 words per minute", level: 2 },
            { code: "12ICT1.4.2.5", description: "Type a passage of written information while looking only at a given written text", level: 2 },
            { code: "12ICT1.4.2.6", description: "Use correct fingers for inputting numbers 0 to 9 and with decimal figures", level: 2 },
            { code: "12ICT1.4.2.7", description: "Type given numeric information correctly at a rate of 50 numbers per minute", level: 2 },
            { code: "12ICT1.4.3.1", description: "Use correct placement of all fingers on appropriate keys of the keyboard", level: 3 },
            { code: "12ICT1.4.3.2", description: "Type a passage of information at a rate of 30 words per minute while looking only at the computer screen", level: 3 },
            { code: "12ICT1.4.3.3", description: "Type a passage of written information correctly at a rate of 30 words per minute while looking only at a given written text", level: 3 }
          ]
        }
      ]
    },
    
    // ============================================
    // STRAND 2: APPLICATION SOFTWARE
    // ============================================
    {
      id: "strand2",
      code: "12ICT2",
      name: "Strand 2: Application Software",
      description: "Skills and understanding of common user applications for specific requirements",
      color: "green",
      majorOutcome: "12ICT2: Upon successful completion of this strand, students are able to demonstrate the skills and the understanding of the use of common user applications such as word-processors, spreadsheet applications and database management systems for specific requirements locally.",
      
      subStrands: [
        {
          id: "substrand2.1",
          code: "12ICT2.1",
          name: "Word Processing II",
          keyOutcome: "Demonstrate understanding of the creation, formatting, editing, and printing of word processing documents",
          slos: [
            { code: "12ICT2.1.1.1", description: "State a key difference between text processors and word processors", level: 1 },
            { code: "12ICT2.1.1.2", description: "Load a word-processing application", level: 1 },
            { code: "12ICT2.1.1.3", description: "Exit a word-processing application", level: 1 },
            { code: "12ICT2.1.1.4", description: "Open an existing document", level: 1 },
            { code: "12ICT2.1.1.5", description: "Create a new blank document", level: 1 },
            { code: "12ICT2.1.1.6", description: "Use help support for word processing", level: 1 },
            { code: "12ICT2.1.1.7", description: "Identify a word processing online tutorial", level: 1 },
            { code: "12ICT2.1.1.8", description: "Identify correct ribbon", level: 1 },
            { code: "12ICT2.1.1.9", description: "Format a character using the ribbon", level: 1 },
            { code: "12ICT2.1.1.10", description: "Use the word-processing menu", level: 1 },
            { code: "12ICT2.1.1.11", description: "Use the word-processing toolbar", level: 1 },
            { code: "12ICT2.1.1.12", description: "State/Identify an editing feature", level: 1 },
            { code: "12ICT2.1.1.13", description: "State/Identify a formatting feature", level: 1 },
            { code: "12ICT2.1.1.14", description: "Use an appropriate font", level: 1 },
            { code: "12ICT2.1.1.15", description: "Produce a document with an inserted feature (Header, footer, page number, image, shapes, tables, symbols, equations)", level: 1 },
            { code: "12ICT2.1.1.16", description: "Create a specific citation", level: 1 },
            { code: "12ICT2.1.1.17", description: "Modify a specific citation", level: 1 },
            { code: "12ICT2.1.1.18", description: "Import a graph from a spreadsheet into a word document", level: 1 },
            { code: "12ICT2.1.1.19", description: "Import a table from a spreadsheet into a word document", level: 1 },
            { code: "12ICT2.1.1.20", description: "Import a database table or a report into a word document", level: 1 },
            { code: "12ICT2.1.1.21", description: "Create a hyperlink within a document", level: 1 },
            { code: "12ICT2.1.1.22", description: "Use part of the mail merge feature", level: 1 },
            { code: "12ICT2.1.1.23", description: "Save a file", level: 1 },
            { code: "12ICT2.1.1.24", description: "Use print preview", level: 1 },
            { code: "12ICT2.1.1.25", description: "Use the printing feature to print a required user file", level: 1 },
            { code: "12ICT2.1.2.1", description: "Outline the main differences between text processors and word processors", level: 2 },
            { code: "12ICT2.1.2.2", description: "Format characters using the ribbon", level: 2 },
            { code: "12ICT2.1.2.3", description: "Apply required editing features on a document", level: 2 },
            { code: "12ICT2.1.2.4", description: "Apply required formatting features on a document", level: 2 },
            { code: "12ICT2.1.2.5", description: "Produce a document with multiple inserted features", level: 2 },
            { code: "12ICT2.1.2.6", description: "Insert a Clip Art object", level: 2 },
            { code: "12ICT2.1.2.7", description: "Manipulate a Clip Art object for a specific document", level: 2 },
            { code: "12ICT2.1.2.8", description: "Apply a footnote", level: 2 },
            { code: "12ICT2.1.2.9", description: "Insert specific citations for a bibliography", level: 2 },
            { code: "12ICT2.1.2.10", description: "Incorporate required imported objects into a word document", level: 2 },
            { code: "12ICT2.1.2.11", description: "Use major components of the mail merge feature", level: 2 },
            { code: "12ICT2.1.2.12", description: "Save all required files", level: 2 },
            { code: "12ICT2.1.2.13", description: "Use the printing feature to print a user file with specified printing properties", level: 2 },
            { code: "12ICT2.1.3.1", description: "Create a document using specific formatting features [paragraphs, Indent, Ruler, Styles etc.]", level: 3 },
            { code: "12ICT2.1.3.2", description: "Produce a document with insert features to meet specific requirements", level: 3 },
            { code: "12ICT2.1.3.3", description: "Produce a document with several imported objects", level: 3 },
            { code: "12ICT2.1.3.4", description: "Produce a customised document using the mail merge feature", level: 3 },
            { code: "12ICT2.1.4.1", description: "Produce a full document using all required formatting features to meet specific requirements", level: 4 },
            { code: "12ICT2.1.4.2", description: "Produce a document with imported objects to meet specific requirements", level: 4 },
            { code: "12ICT2.1.4.3", description: "Produce a complete customised document using the mail merge to meet specific requirements", level: 4 }
          ]
        },
        {
          id: "substrand2.2",
          code: "12ICT2.2",
          name: "Spreadsheets II",
          keyOutcome: "Demonstrate understanding of processing, manipulating, and representing numeric data using the basic functions of a spreadsheet",
          slos: [
            { code: "12ICT2.2.1.1", description: "Navigate a newly created worksheet", level: 1 },
            { code: "12ICT2.2.1.2", description: "Enter a value into a worksheet cell", level: 1 },
            { code: "12ICT2.2.1.3", description: "Edit a value in a cell", level: 1 },
            { code: "12ICT2.2.1.4", description: "Format a spreadsheet cell", level: 1 },
            { code: "12ICT2.2.1.5", description: "State a type of cell referencing", level: 1 },
            { code: "12ICT2.2.1.6", description: "Create a basic spreadsheet formula", level: 1 },
            { code: "12ICT2.2.1.7", description: "State a key benefit of using a formula", level: 1 },
            { code: "12ICT2.2.1.8", description: "Test a spreadsheet formula to verify calculation", level: 1 },
            { code: "12ICT2.2.1.9", description: "Manipulate part of a given data on a worksheet", level: 1 },
            { code: "12ICT2.2.1.10", description: "Manage a spreadsheet file", level: 1 },
            { code: "12ICT2.2.1.11", description: "Identify an appropriate graph for a specific set of data", level: 1 },
            { code: "12ICT2.2.1.12", description: "Export a spreadsheet item to another application", level: 1 },
            { code: "12ICT2.2.1.13", description: "Import part of a database table to a spreadsheet", level: 1 },
            { code: "12ICT2.2.1.14", description: "Perform part of data integrity check", level: 1 },
            { code: "12ICT2.2.2.1", description: "Enter all required values into a worksheet", level: 2 },
            { code: "12ICT2.2.2.2", description: "Apply editing features on a worksheet", level: 2 },
            { code: "12ICT2.2.2.3", description: "Apply formatting features on a worksheet", level: 2 },
            { code: "12ICT2.2.2.4", description: "Apply a cell reference (relative or absolute)", level: 2 },
            { code: "12ICT2.2.2.5", description: "Apply mixed cell references", level: 2 },
            { code: "12ICT2.2.2.6", description: "Produce a worksheet with a complete formula to carry out a basic calculation", level: 2 },
            { code: "12ICT2.2.2.7", description: "Outline key benefits of using formulae", level: 2 },
            { code: "12ICT2.2.2.8", description: "Test all specified formulae in a spreadsheet", level: 2 },
            { code: "12ICT2.2.2.9", description: "Manipulate given data on multiple worksheets", level: 2 },
            { code: "12ICT2.2.2.10", description: "Manage spreadsheet files", level: 2 },
            { code: "12ICT2.2.2.11", description: "Produce a simple spreadsheet graph", level: 2 },
            { code: "12ICT2.2.2.12", description: "Export spreadsheet items to other applications", level: 2 },
            { code: "12ICT2.2.2.13", description: "Import all required data from database tables to a spreadsheet", level: 2 },
            { code: "12ICT2.2.2.14", description: "Perform complete data integrity checks", level: 2 },
            { code: "12ICT2.2.3.1", description: "Produce a formatted worksheet containing values with required formula", level: 3 },
            { code: "12ICT2.2.3.2", description: "Produce a spreadsheet graph to represent a set of data", level: 3 },
            { code: "12ICT2.2.3.3", description: "Apply data integrity practices on a given spreadsheet project", level: 3 },
            { code: "12ICT2.2.4.1", description: "Produce a formatted worksheet containing labels, values and formulae to meet a specific requirement", level: 4 },
            { code: "12ICT2.2.4.2", description: "Produce graphs to visually display the result from analysing a given set of data", level: 4 }
          ]
        },
        {
          id: "substrand2.3",
          code: "12ICT2.3",
          name: "Databases II",
          keyOutcome: "Demonstrate understanding of designing, creating, maintaining and manipulating data in a single table database",
          slos: [
            { code: "12ICT2.3.1.1", description: "State a type of database structure", level: 1 },
            { code: "12ICT2.3.1.2", description: "State a key advantage of a network database", level: 1 },
            { code: "12ICT2.3.1.3", description: "State a key advantage of a relational database", level: 1 },
            { code: "12ICT2.3.1.4", description: "State a key advantage of a hierarchical database", level: 1 },
            { code: "12ICT2.3.1.5", description: "State a known disadvantage of a network database", level: 1 },
            { code: "12ICT2.3.1.6", description: "State a known disadvantage of a relational database", level: 1 },
            { code: "12ICT2.3.1.7", description: "State a known disadvantage of a hierarchical database", level: 1 },
            { code: "12ICT2.3.1.8", description: "Define relational database", level: 1 },
            { code: "12ICT2.3.1.9", description: "State a major concept of relational databases", level: 1 },
            { code: "12ICT2.3.1.10", description: "Define relationships in a relational database", level: 1 },
            { code: "12ICT2.3.1.11", description: "State a known type of relationship in a relational database structure", level: 1 },
            { code: "12ICT2.3.1.12", description: "State a key feature of a type of relationship", level: 1 },
            { code: "12ICT2.3.1.13", description: "Create a part of a basic relational database table using the table wizard", level: 1 },
            { code: "12ICT2.3.1.14", description: "Use the design view to edit a database table", level: 1 },
            { code: "12ICT2.3.1.15", description: "Enter data correctly into the database table", level: 1 },
            { code: "12ICT2.3.1.16", description: "Navigate from one record to another using different views", level: 1 },
            { code: "12ICT2.3.1.17", description: "State the main function of a database form", level: 1 },
            { code: "12ICT2.3.1.18", description: "Create a database form using the form wizard", level: 1 },
            { code: "12ICT2.3.1.19", description: "Create a part of a query", level: 1 },
            { code: "12ICT2.3.1.20", description: "Create a basic report from a query table using the report wizard", level: 1 },
            { code: "12ICT2.3.1.21", description: "Print a report created from a database query", level: 1 },
            { code: "12ICT2.3.1.22", description: "Export data to another user application", level: 1 },
            { code: "12ICT2.3.1.23", description: "Import data from another user application", level: 1 },
            { code: "12ICT2.3.1.24", description: "State an important aspect of data-integrity practices within a database system", level: 1 },
            { code: "12ICT2.3.2.1", description: "Outline three (3) different types of database structures [network, relational, and hierarchical]", level: 2 },
            { code: "12ICT2.3.2.2", description: "Compare known types of database structures", level: 2 },
            { code: "12ICT2.3.2.3", description: "Describe the concept of relational databases", level: 2 },
            { code: "12ICT2.3.2.4", description: "Outline known advantages of relational databases", level: 2 },
            { code: "12ICT2.3.2.5", description: "Outline the known types of relationships within a relational database structure", level: 2 },
            { code: "12ICT2.3.2.6", description: "Create a complete relational database table using the table wizard", level: 2 },
            { code: "12ICT2.3.2.7", description: "Apply appropriate field sizes and types", level: 2 },
            { code: "12ICT2.3.2.8", description: "Apply editing features correctly on database tables", level: 2 },
            { code: "12ICT2.3.2.9", description: "Outline the main functions of a database form", level: 2 },
            { code: "12ICT2.3.2.10", description: "Create multiple forms using the form wizard", level: 2 },
            { code: "12ICT2.3.2.11", description: "Apply formatting tasks to existing forms", level: 2 },
            { code: "12ICT2.3.2.12", description: "Create key components of a query using Structured Query Language (SQL)", level: 2 },
            { code: "12ICT2.3.2.13", description: "Create multiple reports from a database using the report wizard", level: 2 },
            { code: "12ICT2.3.2.14", description: "Apply a data-integrity check within a database", level: 2 },
            { code: "12ICT2.3.2.15", description: "Maintain data-integrity within a database", level: 2 },
            { code: "12ICT2.3.2.16", description: "Outline the process of maintaining data-integrity practices within a database system", level: 2 },
            { code: "12ICT2.3.3.1", description: "Analyze known types of database structures", level: 3 },
            { code: "12ICT2.3.3.2", description: "Explain the key advantages of relational databases", level: 3 },
            { code: "12ICT2.3.3.3", description: "Create a relational database with multiple tables linked together (at least 3 tables)", level: 3 },
            { code: "12ICT2.3.3.4", description: "Create a complete query to present the required output using Structured Query Language (SQL)", level: 3 },
            { code: "12ICT2.3.3.5", description: "Explain how promoting data-integrity practices within a database system assists in the working of a database", level: 3 },
            { code: "12ICT2.3.4.1", description: "Discuss the main advantages of relational databases", level: 4 },
            { code: "12ICT2.3.4.2", description: "Create a relational database with multiple tables linked together to meet specific requirements", level: 4 },
            { code: "12ICT2.3.4.3", description: "Create a complete query with specific conditions to present the required output using Structured Query Language (SQL)", level: 4 },
            { code: "12ICT2.3.4.4", description: "Discuss the impact of promoting data-integrity practices within a database system", level: 4 }
          ]
        }
      ]
    },
    
    // ============================================
    // STRAND 3: GRAPHICS MANIPULATION AND PROGRAMMING
    // ============================================
    {
      id: "strand3",
      code: "12ICT3",
      name: "Strand 3: Graphics Manipulation and Programming",
      description: "Understanding of DTP documents, digital images, presentations and computer graphics",
      color: "purple",
      majorOutcome: "12ICT3: Upon successful completion of this strand, students are able to demonstrate key understanding of simple DTP documents, process digital images, create a presentation and create computer graphics.",
      
      subStrands: [
        {
          id: "substrand3.2",
          code: "12ICT3.2",
          name: "Computer Graphics II",
          keyOutcome: "Demonstrate planning, designing and creating 3D computer graphics",
          slos: [
            { code: "12ICT3.2.1.1", description: "State a key fundamental of 3D graphics", level: 1 },
            { code: "12ICT3.2.1.2", description: "Create a part of a draft sketch of a 3D graphic on paper", level: 1 },
            { code: "12ICT3.2.1.3", description: "Identify an appropriate design application for creating 3D graphics", level: 1 },
            { code: "12ICT3.2.1.4", description: "Produce a part of a basic design of a 3D graphic using a graphic design application", level: 1 },
            { code: "12ICT3.2.1.5", description: "Manipulate a part of a 3D image using an appropriate graphic design tool", level: 1 },
            { code: "12ICT3.2.2.1", description: "Outline key fundamentals of 3D graphics", level: 2 },
            { code: "12ICT3.2.2.2", description: "Create a complete plan of a draft sketch of a 3D graphic on paper", level: 2 },
            { code: "12ICT3.2.2.3", description: "Produce a complete basic design of a 3D graphic using a graphic design application", level: 2 },
            { code: "12ICT3.2.2.4", description: "Manipulate major components of a 3D image using appropriate graphic design tools", level: 2 },
            { code: "12ICT3.2.3.1", description: "Produce a 3D image by manipulating the image using proper design tools", level: 3 },
            { code: "12ICT3.2.4.1", description: "Produce a 3D image by manipulating the image using required design tools to meet the design plan", level: 4 }
          ]
        },
        {
          id: "substrand3.3",
          code: "12ICT3.3",
          name: "Digital Image Processing II",
          keyOutcome: "Demonstrate understanding of manipulating digital images using suitable image processing software",
          slos: [
            { code: "12ICT3.3.1.1", description: "Identify a file type compatible with a specific imaging application", level: 1 },
            { code: "12ICT3.3.1.2", description: "Modify an image property using an imaging application tool", level: 1 },
            { code: "12ICT3.3.1.3", description: "Produce a part of a collage image using a collage maker", level: 1 },
            { code: "12ICT3.3.1.4", description: "Produce part of a panorama image using appropriate imaging tool", level: 1 },
            { code: "12ICT3.3.1.5", description: "Save a processed image", level: 1 },
            { code: "12ICT3.3.1.6", description: "Export a processed image to another application", level: 1 },
            { code: "12ICT3.3.1.7", description: "Print a processed image", level: 1 },
            { code: "12ICT3.3.2.1", description: "List different file types that are compatible with specific imaging application", level: 2 },
            { code: "12ICT3.3.2.2", description: "Modify image properties using imaging tools", level: 2 },
            { code: "12ICT3.3.2.3", description: "Produce a complete collage image using appropriate collage maker", level: 2 },
            { code: "12ICT3.3.2.4", description: "Produce a complete panorama image using appropriate tool", level: 2 },
            { code: "12ICT3.3.2.5", description: "Save multiple processed images", level: 2 },
            { code: "12ICT3.3.2.6", description: "Export processed images to other applications", level: 2 },
            { code: "12ICT3.3.2.7", description: "Print several processed images", level: 2 },
            { code: "12ICT3.3.3.1", description: "Modify image properties using imaging tools according to required specifics", level: 3 },
            { code: "12ICT3.3.3.2", description: "Produce an attractive collage image with enhancement using post processing tools", level: 3 },
            { code: "12ICT3.3.3.3", description: "Produce an attractive panorama image with enhancement using post processing functions", level: 3 }
          ]
        }
      ]
    },
    
    // ============================================
    // STRAND 4: COMMUNICATION AND NETWORK SYSTEMS
    // ============================================
    {
      id: "strand4",
      code: "12ICT4",
      name: "Strand 4: Communication and Network Systems and Issues in ICT",
      description: "Understanding of networking concepts, web pages, and Internet communication and research",
      color: "orange",
      majorOutcome: "12ICT4: Upon successful completion of this strand, students are able to demonstrate key understanding of networking concepts, create web pages, and communicate and research using the Internet.",
      
      subStrands: [
        {
          id: "substrand4.1",
          code: "12ICT4.1",
          name: "Networks II",
          keyOutcome: "Demonstrate understanding of the basic concepts of data networks and connections",
          slos: [
            { code: "12ICT4.1.1.1", description: "State the importance of having a data communication network", level: 1 },
            { code: "12ICT4.1.1.2", description: "State a basic data networking concept", level: 1 },
            { code: "12ICT4.1.1.3", description: "Configure part of the networking settings of a PC", level: 1 },
            { code: "12ICT4.1.1.4", description: "Define TCP/IP", level: 1 },
            { code: "12ICT4.1.1.5", description: "State a key part of TCP/IP", level: 1 },
            { code: "12ICT4.1.1.6", description: "State the function of TCP/IP", level: 1 },
            { code: "12ICT4.1.1.7", description: "Define Domain Name System (DNS)", level: 1 },
            { code: "12ICT4.1.1.8", description: "Identify a common network fault", level: 1 },
            { code: "12ICT4.1.1.9", description: "Install a network card on a PC", level: 1 },
            { code: "12ICT4.1.1.10", description: "Add a networking device to an existing network", level: 1 },
            { code: "12ICT4.1.1.11", description: "Present part of the requirements that can form the basis for connecting a data network", level: 1 },
            { code: "12ICT4.1.2.1", description: "Outline key importance of having a data communication network", level: 2 },
            { code: "12ICT4.1.2.2", description: "Outline basic concepts of data networking", level: 2 },
            { code: "12ICT4.1.2.3", description: "Configure all required networking settings of connected PCs", level: 2 },
            { code: "12ICT4.1.2.4", description: "Outline/List the major components of TCP/IP", level: 2 },
            { code: "12ICT4.1.2.5", description: "Describe the main functions of TCP/IP", level: 2 },
            { code: "12ICT4.1.2.6", description: "Outline/List the functions of DNS", level: 2 },
            { code: "12ICT4.1.2.7", description: "Make working RJ-45 connectors using a crimping tool", level: 2 },
            { code: "12ICT4.1.2.8", description: "Install network cards on PCs and are fully functional", level: 2 },
            { code: "12ICT4.1.2.9", description: "Add multiple networking devices to an existing network", level: 2 },
            { code: "12ICT4.1.2.10", description: "Present all the major components that can form the basis for connecting a data network", level: 2 },
            { code: "12ICT4.1.3.1", description: "Explain the benefits of using a data communication network system", level: 3 },
            { code: "12ICT4.1.3.2", description: "Explain the importance of using TCP/IP", level: 3 },
            { code: "12ICT4.1.3.3", description: "Present a basic connected data network", level: 3 },
            { code: "12ICT4.1.4.1", description: "Discuss major benefits of using a data communication network System for an organisation", level: 4 },
            { code: "12ICT4.1.4.2", description: "Present a fully configured and connected data network", level: 4 }
          ]
        },
        {
          id: "substrand4.2",
          code: "12ICT4.2",
          name: "Web Page II",
          keyOutcome: "Demonstrate understanding of how to plan, design and create a website using free online web hosts",
          slos: [
            { code: "12ICT4.2.1.1", description: "Identify a free web hosting site with cpanel or content management for educational purposes", level: 1 },
            { code: "12ICT4.2.1.2", description: "State a key function of a web hosting site", level: 1 },
            { code: "12ICT4.2.1.3", description: "State an advantage of using free web hosting sites", level: 1 },
            { code: "12ICT4.2.1.4", description: "Define content management system (CMS)", level: 1 },
            { code: "12ICT4.2.1.5", description: "State a key advantage of a content management system for developing websites", level: 1 },
            { code: "12ICT4.2.1.6", description: "Present part of a plan for a website layout", level: 1 },
            { code: "12ICT4.2.1.7", description: "Create an account with a free web hosting provider with panel capability or with any content management console", level: 1 },
            { code: "12ICT4.2.1.8", description: "Create a required webpage for the website using the content management tool", level: 1 },
            { code: "12ICT4.2.1.9", description: "Enter a required text into a page using the content management console", level: 1 },
            { code: "12ICT4.2.1.10", description: "Enter a required graphic into a page using the content management console", level: 1 },
            { code: "12ICT4.2.1.11", description: "Identify an appropriate template for the website", level: 1 },
            { code: "12ICT4.2.1.12", description: "Edit a text item using the content management tool", level: 1 },
            { code: "12ICT4.2.1.13", description: "Edit a graphic item using the content management tool", level: 1 },
            { code: "12ICT4.2.1.14", description: "Publish a specific content", level: 1 },
            { code: "12ICT4.2.1.15", description: "Advertise the website address", level: 1 },
            { code: "12ICT4.2.1.16", description: "Test that the website is operational", level: 1 },
            { code: "12ICT4.2.2.1", description: "Outline key functions of web hosting sites", level: 2 },
            { code: "12ICT4.2.2.2", description: "Outline the main advantages of free web hosting sites", level: 2 },
            { code: "12ICT4.2.2.3", description: "Outline/List key advantages of using a content management system for developing websites", level: 2 },
            { code: "12ICT4.2.2.4", description: "Present the major components of a plan for a website layout with multiple webpages", level: 2 },
            { code: "12ICT4.2.2.5", description: "Create required webpages for the website using the content management tool", level: 2 },
            { code: "12ICT4.2.2.6", description: "Enter some of the required texts into the appropriate pages using the content management console for all created pages", level: 2 },
            { code: "12ICT4.2.2.7", description: "Enter some of the required graphics into the appropriate pages using the content management console", level: 2 },
            { code: "12ICT4.2.2.8", description: "Apply an appropriate template for the website", level: 2 },
            { code: "12ICT4.2.2.9", description: "Apply an appropriate colour scheme for the website", level: 2 },
            { code: "12ICT4.2.2.10", description: "Edit available texts and graphics on the web site", level: 2 },
            { code: "12ICT4.2.2.11", description: "Publish all required contents", level: 2 },
            { code: "12ICT4.2.3.1", description: "Produce a complete plan of the layout of a website with multiple webpages all linked together", level: 3 },
            { code: "12ICT4.2.3.2", description: "Enter required contents to complete the pages using the content management console", level: 3 },
            { code: "12ICT4.2.4.1", description: "Produce a complete plan with all required contents for a website with multiple webpages to meet a specific requirement", level: 4 },
            { code: "12ICT4.2.4.2", description: "Enter all the contents to meet the required design layout for a complete operational website", level: 4 }
          ]
        },
        {
          id: "substrand4.3",
          code: "12ICT4.3",
          name: "The Internet II: Security and Safety",
          keyOutcome: "Demonstrate understanding in observing Internet safety and security practices",
          slos: [
            { code: "12ICT4.3.1.1", description: "State the importance of ICT in society", level: 1 },
            { code: "12ICT4.3.1.2", description: "Define cybercrimes", level: 1 },
            { code: "12ICT4.3.1.3", description: "State an example of cybercrime", level: 1 },
            { code: "12ICT4.3.1.4", description: "State the concern with a specific cybercrime", level: 1 },
            { code: "12ICT4.3.1.5", description: "State a common cybersecurity measure to minimise cybercrimes", level: 1 },
            { code: "12ICT4.3.1.6", description: "State a key feature of a cybersecurity measure", level: 1 },
            { code: "12ICT4.3.1.7", description: "Define HTTPS", level: 1 },
            { code: "12ICT4.3.1.8", description: "State an appropriate cyber-safety practice", level: 1 },
            { code: "12ICT4.3.1.9", description: "Define data security", level: 1 },
            { code: "12ICT4.3.1.10", description: "State the importance of data security", level: 1 },
            { code: "12ICT4.3.1.11", description: "State the importance of maintaining data integrity", level: 1 },
            { code: "12ICT4.3.2.1", description: "Outline some of the key importance of ICT in society", level: 2 },
            { code: "12ICT4.3.2.2", description: "List examples of cybercrimes", level: 2 },
            { code: "12ICT4.3.2.3", description: "Outline the concerns with cybercrimes", level: 2 },
            { code: "12ICT4.3.2.4", description: "List some of the common cybersecurity measures to minimise cybercrimes", level: 2 },
            { code: "12ICT4.3.2.5", description: "Outline HTTPS in terms of website security", level: 2 },
            { code: "12ICT4.3.2.6", description: "Outline/List key features of a cybersecurity measure", level: 2 },
            { code: "12ICT4.3.2.7", description: "Outline/List some appropriate cyber-safety practices", level: 2 },
            { code: "12ICT4.3.2.8", description: "Describe the processes involved in data security", level: 2 },
            { code: "12ICT4.3.2.9", description: "Outline key importance of maintaining data integrity", level: 2 },
            { code: "12ICT4.3.3.1", description: "Explain why cybercrimes is a concern", level: 3 },
            { code: "12ICT4.3.3.2", description: "Explain the role of the HTTPS protocols in terms of website security", level: 3 },
            { code: "12ICT4.3.3.3", description: "Explain how cybersecurity measures protect computer systems", level: 3 },
            { code: "12ICT4.3.3.4", description: "Explain the importance of data security", level: 3 },
            { code: "12ICT4.3.4.1", description: "Discuss the impact of cybercrimes on organisations and computer systems", level: 4 },
            { code: "12ICT4.3.4.2", description: "Discuss how cybersecurity measures have strengthened computer systems", level: 4 },
            { code: "12ICT4.3.4.3", description: "Discuss the impact of data security on how organisations work", level: 4 }
          ]
        }
      ]
    }
  ]
};

// ============================================
// YEARLY PLANNER - Term by Term Breakdown
// ============================================
export const year12ICTYearlyPlanner = {
  term1: {
    name: "Term 1",
    weeks: "Weeks 1-10",
    focus: "PC Systems & Hardware Maintenance",
    units: [
      {
        id: "t1u1",
        title: "Unit 1: PC Hardware Components",
        weeks: "Weeks 1-3",
        strand: "Strand 1: PC Systems",
        subStrand: "Computer Hardware Maintenance",
        slosCovered: ["12ICT1.1.1.1", "12ICT1.1.2.1", "12ICT1.1.2.2", "12ICT1.1.2.3"],
        topics: [
          "Introduction to PC hardware components",
          "Identifying major hardware: CPU, RAM, HDD/SSD, Motherboard",
          "Common PC ports: USB, HDMI, VGA, Ethernet",
          "Common PC cables and connectors"
        ],
        activities: [
          "Hardware identification practical",
          "Disassemble and reassemble a PC",
          "Port and cable matching exercise"
        ],
        assessment: "Practical: Identify and label PC components"
      },
      {
        id: "t1u2",
        title: "Unit 2: Building a PC",
        weeks: "Weeks 4-6",
        strand: "Strand 1: PC Systems",
        subStrand: "Computer Hardware Maintenance",
        slosCovered: ["12ICT1.1.3.1", "12ICT1.1.4.1", "12ICT1.1.1.2"],
        topics: [
          "PC assembly safety procedures",
          "Step-by-step PC building process",
          "Power-on testing procedures",
          "Basic troubleshooting during assembly"
        ],
        activities: [
          "Group PC building project",
          "Power supply installation",
          "CPU and RAM installation",
          "First boot testing"
        ],
        assessment: "Project: Build a working PC"
      },
      {
        id: "t1u3",
        title: "Unit 3: Operating System Installation",
        weeks: "Weeks 7-9",
        strand: "Strand 1: PC Systems",
        subStrand: "Computer Hardware Maintenance",
        slosCovered: ["12ICT1.1.1.3", "12ICT1.1.1.4", "12ICT1.1.2.4", "12ICT1.1.3.2", "12ICT1.1.1.5"],
        topics: [
          "Types of operating systems",
          "Windows installation process",
          "Driver installation",
          "Testing OS functionality"
        ],
        activities: [
          "Install Windows on lab PCs",
          "Device driver installation",
          "System configuration"
        ],
        assessment: "Practical: Complete OS installation"
      },
      {
        id: "t1u4",
        title: "Unit 4: Term 1 Review & Assessment",
        weeks: "Week 10",
        strand: "Strand 1: PC Systems",
        subStrand: "Computer Hardware Maintenance",
        topics: ["Review of all Term 1 content"],
        activities: ["Revision exercises", "Practice practical exam"],
        assessment: "Term 1 Theory & Practical Exam"
      }
    ]
  },
  term2: {
    name: "Term 2",
    weeks: "Weeks 11-20",
    focus: "Application Software (Word Processing & Spreadsheets)",
    units: [
      {
        id: "t2u1",
        title: "Unit 5: Advanced Word Processing",
        weeks: "Weeks 11-13",
        strand: "Strand 2: Application Software",
        subStrand: "Word Processing II",
        slosCovered: ["12ICT2.1.1.1", "12ICT2.1.2.1", "12ICT2.1.2.2", "12ICT2.1.2.3", "12ICT2.1.2.4", "12ICT2.1.3.1"],
        topics: [
          "Text vs Word processors comparison",
          "Advanced formatting: styles, paragraphs, indents",
          "Headers, footers, page numbers",
          "Tables and images"
        ],
        activities: [
          "Create formatted business letters",
          "Design a school newsletter",
          "Format a formal report"
        ],
        assessment: "Project: Professional document creation"
      },
      {
        id: "t2u2",
        title: "Unit 6: Mail Merge & Citations",
        weeks: "Weeks 14-15",
        strand: "Strand 2: Application Software",
        subStrand: "Word Processing II",
        slosCovered: ["12ICT2.1.1.22", "12ICT2.1.2.11", "12ICT2.1.3.4", "12ICT2.1.4.3", "12ICT2.1.1.16", "12ICT2.1.2.9"],
        topics: [
          "Mail merge fundamentals",
          "Creating data sources",
          "Citations and bibliographies",
          "Academic referencing"
        ],
        activities: [
          "Create personalised letters using mail merge",
          "Add citations to a research document",
          "Generate bibliography"
        ],
        assessment: "Practical: Mail merge and citations"
      },
      {
        id: "t2u3",
        title: "Unit 7: Spreadsheet Fundamentals",
        weeks: "Weeks 16-18",
        strand: "Strand 2: Application Software",
        subStrand: "Spreadsheets II",
        slosCovered: ["12ICT2.2.1.1", "12ICT2.2.1.2", "12ICT2.2.2.1", "12ICT2.2.1.4", "12ICT2.2.2.3", "12ICT2.2.1.6", "12ICT2.2.2.6"],
        topics: [
          "Spreadsheet navigation and data entry",
          "Cell formatting and styles",
          "Basic formulas: SUM, AVERAGE, COUNT",
          "Cell referencing (relative, absolute, mixed)"
        ],
        activities: [
          "Create a class grade book",
          "Build a simple budget spreadsheet",
          "Formula practice exercises"
        ],
        assessment: "Practical: Spreadsheet with formulas"
      },
      {
        id: "t2u4",
        title: "Unit 8: Charts & Data Analysis",
        weeks: "Weeks 19-20",
        strand: "Strand 2: Application Software",
        subStrand: "Spreadsheets II",
        slosCovered: ["12ICT2.2.1.11", "12ICT2.2.2.11", "12ICT2.2.3.2", "12ICT2.2.4.2"],
        topics: [
          "Chart types and when to use them",
          "Creating and formatting charts",
          "Data analysis using graphs",
          "Exporting data"
        ],
        activities: [
          "Create charts from school data",
          "Analyze sales data graphically",
          "Export charts to Word documents"
        ],
        assessment: "Term 2 Theory & Practical Exam"
      }
    ]
  },
  term3: {
    name: "Term 3",
    weeks: "Weeks 21-30",
    focus: "Databases, Graphics & Programming",
    units: [
      {
        id: "t3u1",
        title: "Unit 9: Database Fundamentals",
        weeks: "Weeks 21-23",
        strand: "Strand 2: Application Software",
        subStrand: "Databases II",
        slosCovered: ["12ICT2.3.1.1", "12ICT2.3.2.1", "12ICT2.3.1.8", "12ICT2.3.2.3", "12ICT2.3.1.13", "12ICT2.3.2.6"],
        topics: [
          "Database structures: network, relational, hierarchical",
          "Relational database concepts",
          "Creating tables using wizard",
          "Data entry and navigation"
        ],
        activities: [
          "Create a student database",
          "Design tables for a library system",
          "Enter and edit records"
        ],
        assessment: "Practical: Create a relational database"
      },
      {
        id: "t3u2",
        title: "Unit 10: Forms, Queries & Reports",
        weeks: "Weeks 24-25",
        strand: "Strand 2: Application Software",
        subStrand: "Databases II",
        slosCovered: ["12ICT2.3.1.18", "12ICT2.3.2.10", "12ICT2.3.1.19", "12ICT2.3.2.12", "12ICT2.3.3.4", "12ICT2.3.1.20", "12ICT2.3.2.13"],
        topics: [
          "Creating forms using form wizard",
          "SQL query basics",
          "Creating reports",
          "Data integrity"
        ],
        activities: [
          "Design data entry forms",
          "Write simple SQL queries",
          "Generate formatted reports"
        ],
        assessment: "Practical: Database with forms and queries"
      },
      {
        id: "t3u3",
        title: "Unit 11: Digital Image Processing",
        weeks: "Weeks 26-27",
        strand: "Strand 3: Graphics",
        subStrand: "Digital Image Processing II",
        slosCovered: ["12ICT3.3.1.1", "12ICT3.3.2.1", "12ICT3.3.1.2", "12ICT3.3.2.2", "12ICT3.3.1.3", "12ICT3.3.2.3", "12ICT3.3.3.2"],
        topics: [
          "Image file formats (JPG, PNG, GIF, BMP)",
          "Basic image editing",
          "Creating collages",
          "Image enhancement"
        ],
        activities: [
          "Edit photos using GIMP/Photoshop",
          "Create a photo collage",
          "Enhance old photos"
        ],
        assessment: "Project: Digital image portfolio"
      },
      {
        id: "t3u4",
        title: "Unit 12: Programming Fundamentals",
        weeks: "Weeks 28-30",
        strand: "Strand 1: PC Systems",
        subStrand: "Programming II",
        slosCovered: ["12ICT1.3.1.1", "12ICT1.3.2.1", "12ICT1.3.1.3", "12ICT1.3.1.4", "12ICT1.3.1.5", "12ICT1.3.1.15", "12ICT1.3.2.7"],
        topics: [
          "Structured programming concepts",
          "Sequence, selection, repetition",
          "Variables and data types",
          "Basic coding in Python"
        ],
        activities: [
          "Write simple Python programs",
          "Practice variable declarations",
          "Create programs with loops"
        ],
        assessment: "Term 3 Theory & Practical Exam"
      }
    ]
  },
  term4: {
    name: "Term 4",
    weeks: "Weeks 31-40",
    focus: "Networking, Web Development & Security",
    units: [
      {
        id: "t4u1",
        title: "Unit 13: Computer Networks",
        weeks: "Weeks 31-33",
        strand: "Strand 4: Communication & Networks",
        subStrand: "Networks II",
        slosCovered: ["12ICT4.1.1.1", "12ICT4.1.2.1", "12ICT4.1.3.1", "12ICT4.1.1.4", "12ICT4.1.2.4", "12ICT4.1.1.7", "12ICT4.1.2.6"],
        topics: [
          "Importance of data networks",
          "TCP/IP protocols",
          "DNS and IP addressing",
          "Network hardware"
        ],
        activities: [
          "Configure network settings",
          "Create RJ-45 connectors",
          "Set up a small network"
        ],
        assessment: "Practical: Network configuration"
      },
      {
        id: "t4u2",
        title: "Unit 14: Web Page Creation",
        weeks: "Weeks 34-36",
        strand: "Strand 4: Communication & Networks",
        subStrand: "Web Page II",
        slosCovered: ["12ICT4.2.1.1", "12ICT4.2.1.4", "12ICT4.2.2.3", "12ICT4.2.1.7", "12ICT4.2.1.8", "12ICT4.2.2.5", "12ICT4.2.3.1"],
        topics: [
          "Web hosting basics",
          "Content Management Systems",
          "Website planning",
          "Creating webpages"
        ],
        activities: [
          "Create free hosting account",
          "Design website layout",
          "Build multi-page website"
        ],
        assessment: "Project: Create a personal website"
      },
      {
        id: "t4u3",
        title: "Unit 15: Internet Security & Safety",
        weeks: "Weeks 37-38",
        strand: "Strand 4: Communication & Networks",
        subStrand: "The Internet II: Security and Safety",
        slosCovered: ["12ICT4.3.1.2", "12ICT4.3.2.2", "12ICT4.3.3.1", "12ICT4.3.4.1", "12ICT4.3.1.5", "12ICT4.3.2.4", "12ICT4.3.3.3", "12ICT4.3.1.9", "12ICT4.3.3.4"],
        topics: [
          "Cybercrimes and their impact",
          "Cybersecurity measures",
          "HTTPS and secure browsing",
          "Data security principles"
        ],
        activities: [
          "Research common cybercrimes",
          "Analyze security measures",
          "Create a cyber safety poster"
        ],
        assessment: "Essay: Impact of cybercrimes"
      },
      {
        id: "t4u4",
        title: "Unit 16: Final Review & Examinations",
        weeks: "Weeks 39-40",
        strand: "All Strands",
        subStrand: "All",
        topics: ["Comprehensive review of Year 12 ICT"],
        activities: ["Revision sessions", "Practice exams", "Portfolio completion"],
        assessment: "Final Year Examination"
      }
    ]
  }
};

// ============================================
// TEST PAPERS
// ============================================
export const year12ICTTests = {
  term1: [
    {
      id: "y12ict-t1-test1",
      title: "Year 12 ICT - Term 1 Test: PC Hardware",
      term: "term1",
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer ALL questions",
        "Write your answers clearly",
        "Show all working where required"
      ],
      sections: [
        {
          name: "Section A: Multiple Choice",
          marks: 15,
          questions: [
            { id: 1, question: "Which component is considered the 'brain' of the computer?", options: ["A. RAM", "B. CPU", "C. Hard Drive", "D. Motherboard"], answer: "B", marks: 1 },
            { id: 2, question: "What type of memory loses its data when power is turned off?", options: ["A. ROM", "B. Hard Drive", "C. RAM", "D. SSD"], answer: "C", marks: 1 },
            { id: 3, question: "Which port is commonly used for connecting a printer?", options: ["A. HDMI", "B. VGA", "C. USB", "D. Ethernet"], answer: "C", marks: 1 },
            { id: 4, question: "What does SSD stand for?", options: ["A. Solid State Drive", "B. System Storage Device", "C. Super Speed Disk", "D. Secure System Drive"], answer: "A", marks: 1 },
            { id: 5, question: "Which cable is used for video output?", options: ["A. SATA", "B. HDMI", "C. RJ-45", "D. Power Cable"], answer: "B", marks: 1 },
            { id: 6, question: "RAM is measured in:", options: ["A. Hertz", "B. Gigabytes", "C. Watts", "D. RPM"], answer: "B", marks: 1 },
            { id: 7, question: "The motherboard connects all components using:", options: ["A. WiFi", "B. Cables only", "C. Buses", "D. Bluetooth"], answer: "C", marks: 1 },
            { id: 8, question: "Which is NOT a type of PC port?", options: ["A. USB", "B. HDMI", "C. RAM", "D. VGA"], answer: "C", marks: 1 },
            { id: 9, question: "The power supply unit converts:", options: ["A. DC to AC", "B. AC to DC", "C. Digital to analog", "D. Analog to digital"], answer: "B", marks: 1 },
            { id: 10, question: "Which component stores the operating system permanently?", options: ["A. RAM", "B. CPU Cache", "C. Hard Drive/SSD", "D. Graphics Card"], answer: "C", marks: 1 },
            { id: 11, question: "BIOS stands for:", options: ["A. Basic Input Output System", "B. Binary Input Output System", "C. Basic Internal Operating System", "D. Binary Internal Operating System"], answer: "A", marks: 1 },
            { id: 12, question: "The Ethernet port uses which type of connector?", options: ["A. USB Type-A", "B. RJ-45", "C. HDMI", "D. DisplayPort"], answer: "B", marks: 1 },
            { id: 13, question: "What is the function of thermal paste?", options: ["A. Lubricates fans", "B. Transfers heat from CPU to heatsink", "C. Powers the CPU", "D. Stores data"], answer: "B", marks: 1 },
            { id: 14, question: "Which is the fastest storage type?", options: ["A. HDD", "B. SSD", "C. NVMe SSD", "D. Floppy disk"], answer: "C", marks: 1 },
            { id: 15, question: "A GPU is responsible for:", options: ["A. Sound processing", "B. Network connections", "C. Graphics processing", "D. Storing files"], answer: "C", marks: 1 }
          ]
        },
        {
          name: "Section B: Short Answer",
          marks: 20,
          questions: [
            { id: 16, question: "List FOUR major hardware components of a PC.", marks: 4 },
            { id: 17, question: "Explain the difference between RAM and ROM.", marks: 4 },
            { id: 18, question: "Identify THREE common PC ports and state their uses.", marks: 6 },
            { id: 19, question: "Describe the steps to safely install RAM in a computer.", marks: 6 }
          ]
        },
        {
          name: "Section C: Extended Response",
          marks: 15,
          questions: [
            { id: 20, question: "A school wants to build 10 new computers for their ICT lab. As an ICT technician:\n(a) List all the hardware components they would need to purchase. (5 marks)\n(b) Explain the step-by-step process of assembling one complete PC. (10 marks)", marks: 15 }
          ]
        }
      ]
    }
  ],
  term2: [
    {
      id: "y12ict-t2-test1",
      title: "Year 12 ICT - Term 2 Test: Application Software",
      term: "term2",
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer ALL questions",
        "Write your answers clearly",
        "Practical tasks to be completed on computer"
      ],
      sections: [
        {
          name: "Section A: Multiple Choice",
          marks: 15,
          questions: [
            { id: 1, question: "Which is a word processing application?", options: ["A. Excel", "B. Word", "C. Access", "D. Paint"], answer: "B", marks: 1 },
            { id: 2, question: "In Excel, a cell reference like $A$1 is called:", options: ["A. Relative", "B. Absolute", "C. Mixed", "D. Dynamic"], answer: "B", marks: 1 },
            { id: 3, question: "Mail merge is used to:", options: ["A. Edit images", "B. Create personalised documents", "C. Calculate numbers", "D. Build databases"], answer: "B", marks: 1 },
            { id: 4, question: "Which formula calculates the average?", options: ["A. =SUM()", "B. =COUNT()", "C. =AVERAGE()", "D. =MAX()"], answer: "C", marks: 1 },
            { id: 5, question: "A citation is used to:", options: ["A. Add page numbers", "B. Reference sources", "C. Insert images", "D. Format text"], answer: "B", marks: 1 },
            { id: 6, question: "Which chart is best for showing parts of a whole?", options: ["A. Line chart", "B. Bar chart", "C. Pie chart", "D. Scatter plot"], answer: "C", marks: 1 },
            { id: 7, question: "Headers appear at the:", options: ["A. Bottom of each page", "B. Top of each page", "C. Left margin", "D. Right margin"], answer: "B", marks: 1 },
            { id: 8, question: "In spreadsheets, B2:B10 represents:", options: ["A. A single cell", "B. A row", "C. A column range", "D. The entire sheet"], answer: "C", marks: 1 },
            { id: 9, question: "What does the formula =A1+B1 do?", options: ["A. Subtracts cells", "B. Adds two cells", "C. Multiplies cells", "D. Divides cells"], answer: "B", marks: 1 },
            { id: 10, question: "Which key is used to confirm cell entry in Excel?", options: ["A. Shift", "B. Ctrl", "C. Enter", "D. Delete"], answer: "C", marks: 1 },
            { id: 11, question: "Footnotes appear at the:", options: ["A. Top of the page", "B. Bottom of the page", "C. End of document", "D. In margins"], answer: "B", marks: 1 },
            { id: 12, question: "Which is a spreadsheet file extension?", options: ["A. .docx", "B. .xlsx", "C. .pptx", "D. .accdb"], answer: "B", marks: 1 },
            { id: 13, question: "The =COUNT() function counts:", options: ["A. Text only", "B. Numbers only", "C. All cells", "D. Empty cells"], answer: "B", marks: 1 },
            { id: 14, question: "Which ribbon contains font formatting options in Word?", options: ["A. Insert", "B. Home", "C. Page Layout", "D. Review"], answer: "B", marks: 1 },
            { id: 15, question: "A hyperlink allows you to:", options: ["A. Format text", "B. Jump to another location", "C. Add borders", "D. Check spelling"], answer: "B", marks: 1 }
          ]
        },
        {
          name: "Section B: Short Answer",
          marks: 20,
          questions: [
            { id: 16, question: "Explain the difference between text processors and word processors.", marks: 4 },
            { id: 17, question: "List FOUR formatting features available in a word processor.", marks: 4 },
            { id: 18, question: "Explain the difference between relative and absolute cell referencing with examples.", marks: 6 },
            { id: 19, question: "Describe the steps to create a basic chart in Excel.", marks: 6 }
          ]
        },
        {
          name: "Section C: Practical Task",
          marks: 15,
          questions: [
            { id: 20, question: "Create a spreadsheet for a small shop with:\n- Column A: Product names (5 items)\n- Column B: Prices\n- Column C: Quantity sold\n- Column D: Total sales (using formula)\n- Row for Grand Total\n- Create a bar chart showing sales by product", marks: 15 }
          ]
        }
      ]
    }
  ],
  term3: [
    {
      id: "y12ict-t3-test1",
      title: "Year 12 ICT - Term 3 Test: Databases & Graphics",
      term: "term3",
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer ALL questions",
        "Write your answers clearly",
        "SQL queries must use correct syntax"
      ],
      sections: [
        {
          name: "Section A: Multiple Choice",
          marks: 15,
          questions: [
            { id: 1, question: "A relational database stores data in:", options: ["A. Folders", "B. Tables", "C. Documents", "D. Slides"], answer: "B", marks: 1 },
            { id: 2, question: "SQL stands for:", options: ["A. Simple Query Language", "B. Structured Query Language", "C. System Query Logic", "D. Standard Query List"], answer: "B", marks: 1 },
            { id: 3, question: "Which relationship type means one record relates to many?", options: ["A. One-to-One", "B. Many-to-Many", "C. One-to-Many", "D. None"], answer: "C", marks: 1 },
            { id: 4, question: "A database form is used for:", options: ["A. Storing images", "B. Data entry", "C. Printing only", "D. Calculations"], answer: "B", marks: 1 },
            { id: 5, question: "Which SQL command retrieves data?", options: ["A. INSERT", "B. UPDATE", "C. SELECT", "D. DELETE"], answer: "C", marks: 1 },
            { id: 6, question: "PNG is a:", options: ["A. Database format", "B. Image format", "C. Document format", "D. Video format"], answer: "B", marks: 1 },
            { id: 7, question: "A collage is:", options: ["A. A single image", "B. Multiple images combined", "C. A video", "D. An animation"], answer: "B", marks: 1 },
            { id: 8, question: "Which is NOT an image file format?", options: ["A. JPEG", "B. GIF", "C. DOCX", "D. PNG"], answer: "C", marks: 1 },
            { id: 9, question: "A primary key is:", options: ["A. The first column", "B. A unique identifier", "C. A password", "D. A foreign key"], answer: "B", marks: 1 },
            { id: 10, question: "Which database object displays formatted output?", options: ["A. Table", "B. Query", "C. Form", "D. Report"], answer: "D", marks: 1 },
            { id: 11, question: "Image resolution is measured in:", options: ["A. Bytes", "B. Pixels", "C. Hertz", "D. Watts"], answer: "B", marks: 1 },
            { id: 12, question: "A panorama image is:", options: ["A. A small thumbnail", "B. A wide-angle view", "C. A 3D image", "D. An animated image"], answer: "B", marks: 1 },
            { id: 13, question: "Data integrity ensures data is:", options: ["A. Colorful", "B. Accurate and consistent", "C. Compressed", "D. Encrypted"], answer: "B", marks: 1 },
            { id: 14, question: "Which database type uses nodes and branches?", options: ["A. Relational", "B. Network", "C. Hierarchical", "D. Flat file"], answer: "C", marks: 1 },
            { id: 15, question: "GIMP is a:", options: ["A. Database software", "B. Image editing software", "C. Spreadsheet", "D. Web browser"], answer: "B", marks: 1 }
          ]
        },
        {
          name: "Section B: Short Answer",
          marks: 20,
          questions: [
            { id: 16, question: "Compare THREE types of database structures (network, relational, hierarchical).", marks: 6 },
            { id: 17, question: "Write a SQL query to select all students from a 'Students' table where the grade is 'A'.", marks: 4 },
            { id: 18, question: "List FOUR common image file formats and explain when each should be used.", marks: 4 },
            { id: 19, question: "Explain the importance of data integrity in a database system.", marks: 6 }
          ]
        },
        {
          name: "Section C: Extended Response",
          marks: 15,
          questions: [
            { id: 20, question: "Design a database for a school library:\n(a) List THREE tables you would create with their fields. (6 marks)\n(b) Identify the primary keys for each table. (3 marks)\n(c) Describe the relationships between the tables. (6 marks)", marks: 15 }
          ]
        }
      ]
    }
  ],
  term4: [
    {
      id: "y12ict-t4-test1",
      title: "Year 12 ICT - Term 4 Test: Networks & Security",
      term: "term4",
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer ALL questions",
        "Write your answers clearly",
        "Include diagrams where appropriate"
      ],
      sections: [
        {
          name: "Section A: Multiple Choice",
          marks: 15,
          questions: [
            { id: 1, question: "TCP/IP stands for:", options: ["A. Technical Control Protocol/Internet Protocol", "B. Transmission Control Protocol/Internet Protocol", "C. Transfer Control Process/Internet Protocol", "D. Technical Computer Protocol/Internet Protocol"], answer: "B", marks: 1 },
            { id: 2, question: "DNS converts:", options: ["A. Images to text", "B. Domain names to IP addresses", "C. Files to folders", "D. Audio to video"], answer: "B", marks: 1 },
            { id: 3, question: "Which cable is used for Ethernet connections?", options: ["A. HDMI", "B. USB", "C. CAT5/CAT6", "D. VGA"], answer: "C", marks: 1 },
            { id: 4, question: "A CMS is used for:", options: ["A. Hardware repair", "B. Website management", "C. Network security", "D. Email"], answer: "B", marks: 1 },
            { id: 5, question: "HTTPS provides:", options: ["A. Faster browsing", "B. Secure communication", "C. Free hosting", "D. Video streaming"], answer: "B", marks: 1 },
            { id: 6, question: "A firewall:", options: ["A. Heats the computer", "B. Protects from unauthorized access", "C. Speeds up internet", "D. Stores files"], answer: "B", marks: 1 },
            { id: 7, question: "Phishing is a type of:", options: ["A. Fishing game", "B. Cybercrime", "C. Network protocol", "D. Web hosting"], answer: "B", marks: 1 },
            { id: 8, question: "An IP address identifies:", options: ["A. A user's name", "B. A device on a network", "C. A website's color", "D. A file type"], answer: "B", marks: 1 },
            { id: 9, question: "Which is NOT a network topology?", options: ["A. Star", "B. Bus", "C. Ring", "D. Square"], answer: "D", marks: 1 },
            { id: 10, question: "A router:", options: ["A. Connects networks", "B. Only creates documents", "C. Stores images", "D. Plays videos"], answer: "A", marks: 1 },
            { id: 11, question: "WordPress is an example of:", options: ["A. Spreadsheet", "B. CMS", "C. Database", "D. Operating system"], answer: "B", marks: 1 },
            { id: 12, question: "Data security protects:", options: ["A. Computer color", "B. Information from unauthorized access", "C. Screen size", "D. Fan speed"], answer: "B", marks: 1 },
            { id: 13, question: "A switch connects:", options: ["A. Power cables", "B. Devices within a network", "C. Keyboards", "D. Monitors"], answer: "B", marks: 1 },
            { id: 14, question: "RJ-45 is a type of:", options: ["A. Software", "B. Network connector", "C. Operating system", "D. Browser"], answer: "B", marks: 1 },
            { id: 15, question: "Ransomware:", options: ["A. Speeds up computers", "B. Encrypts files for ransom", "C. Free software", "D. Networking tool"], answer: "B", marks: 1 }
          ]
        },
        {
          name: "Section B: Short Answer",
          marks: 20,
          questions: [
            { id: 16, question: "Explain the importance of TCP/IP in computer networks.", marks: 4 },
            { id: 17, question: "List FOUR benefits of using a data communication network for an organization.", marks: 4 },
            { id: 18, question: "Describe FIVE common cybersecurity measures to protect computer systems.", marks: 5 },
            { id: 19, question: "Explain what a Content Management System (CMS) is and list THREE advantages of using one.", marks: 7 }
          ]
        },
        {
          name: "Section C: Extended Response",
          marks: 15,
          questions: [
            { id: 20, question: "A new business wants to set up a computer network and website:\n(a) Describe the hardware components needed for a basic office network. (5 marks)\n(b) Explain the steps to create a website using a CMS. (5 marks)\n(c) Discuss cybersecurity measures the business should implement. (5 marks)", marks: 15 }
          ]
        }
      ]
    }
  ]
};

// ============================================
// WORKSHEETS
// ============================================
export const year12ICTWorksheets = {
  strand1: [
    {
      id: "ws-y12-s1-01",
      title: "Worksheet 1: PC Hardware Identification",
      strand: "Strand 1",
      subStrand: "Computer Hardware Maintenance",
      slosCovered: ["12ICT1.1.1.1", "12ICT1.1.2.1"],
      instructions: "Complete the following activities to demonstrate your understanding of PC hardware components.",
      activities: [
        {
          type: "labeling",
          title: "Activity 1: Label the PC Components",
          description: "Draw and label the following PC components: CPU, RAM, Motherboard, Hard Drive, Power Supply, Graphics Card"
        },
        {
          type: "matching",
          title: "Activity 2: Match Components to Functions",
          pairs: [
            { component: "CPU", function: "Processes all computer instructions" },
            { component: "RAM", function: "Temporary memory for active programs" },
            { component: "Hard Drive", function: "Permanent storage of files" },
            { component: "Motherboard", function: "Connects all components" },
            { component: "PSU", function: "Provides power to components" }
          ]
        },
        {
          type: "short_answer",
          title: "Activity 3: Short Answer",
          questions: [
            "What is the difference between RAM and ROM?",
            "Why is the CPU called the 'brain' of the computer?",
            "What happens if a computer doesn't have enough RAM?"
          ]
        }
      ]
    },
    {
      id: "ws-y12-s1-02",
      title: "Worksheet 2: PC Ports and Cables",
      strand: "Strand 1",
      subStrand: "Computer Hardware Maintenance",
      slosCovered: ["12ICT1.1.2.2", "12ICT1.1.2.3"],
      instructions: "Identify and describe common PC ports and cables.",
      activities: [
        {
          type: "identification",
          title: "Activity 1: Port Identification",
          description: "Identify each port shown in the images: USB, HDMI, VGA, Ethernet, Audio Jack, DisplayPort"
        },
        {
          type: "table_completion",
          title: "Activity 2: Complete the Table",
          headers: ["Port/Cable", "Common Use", "Data Transfer Speed"],
          rows: [
            ["USB 3.0", "___", "___"],
            ["HDMI", "___", "___"],
            ["Ethernet (CAT6)", "___", "___"],
            ["SATA", "___", "___"]
          ]
        }
      ]
    },
    {
      id: "ws-y12-s1-03",
      title: "Worksheet 3: Programming Basics",
      strand: "Strand 1",
      subStrand: "Programming II",
      slosCovered: ["12ICT1.3.1.1", "12ICT1.3.1.3", "12ICT1.3.1.4", "12ICT1.3.1.5"],
      instructions: "Practice basic programming concepts.",
      activities: [
        {
          type: "definition",
          title: "Activity 1: Define Programming Terms",
          terms: ["Structured Programming", "Sequence", "Selection", "Repetition", "Variable", "Array"]
        },
        {
          type: "code_completion",
          title: "Activity 2: Complete the Code",
          description: "Fill in the blanks to complete the Python code",
          code: `# Declare a variable
name = _____
age = _____

# Print a message
print("Hello, " + _____)

# Selection (if statement)
if age >= 18:
    print("You are an _____")
else:
    print("You are a _____")

# Repetition (loop)
for i in range(5):
    print("Count: " + str(___))`
        }
      ]
    }
  ],
  strand2: [
    {
      id: "ws-y12-s2-01",
      title: "Worksheet 4: Word Processing Features",
      strand: "Strand 2",
      subStrand: "Word Processing II",
      slosCovered: ["12ICT2.1.1.12", "12ICT2.1.1.13", "12ICT2.1.2.3", "12ICT2.1.2.4"],
      instructions: "Practice word processing formatting features.",
      activities: [
        {
          type: "practical",
          title: "Activity 1: Document Formatting",
          tasks: [
            "Create a new document with the title 'My School Report'",
            "Apply heading styles to the title",
            "Create 3 paragraphs with different formatting",
            "Add page numbers and headers",
            "Insert a table with 3 columns and 4 rows"
          ]
        },
        {
          type: "matching",
          title: "Activity 2: Match Features to Functions",
          pairs: [
            { feature: "Bold", function: "Makes text darker and thicker" },
            { feature: "Indent", function: "Moves text away from the margin" },
            { feature: "Line Spacing", function: "Controls space between lines" },
            { feature: "Header", function: "Text at top of every page" },
            { feature: "Footer", function: "Text at bottom of every page" }
          ]
        }
      ]
    },
    {
      id: "ws-y12-s2-02",
      title: "Worksheet 5: Spreadsheet Formulas",
      strand: "Strand 2",
      subStrand: "Spreadsheets II",
      slosCovered: ["12ICT2.2.1.6", "12ICT2.2.2.4", "12ICT2.2.2.6"],
      instructions: "Practice creating and using spreadsheet formulas.",
      activities: [
        {
          type: "formula_writing",
          title: "Activity 1: Write the Formulas",
          problems: [
            "Sum cells A1 to A10",
            "Calculate average of B1 to B5",
            "Count numbers in range C1:C20",
            "Find the maximum value in D1:D15",
            "Multiply E1 by F1 and add G1"
          ]
        },
        {
          type: "practical",
          title: "Activity 2: Create a Budget Spreadsheet",
          tasks: [
            "Create columns: Item, Budget, Actual, Difference",
            "Add 5 expense items",
            "Use formulas to calculate difference",
            "Add totals row with SUM formulas",
            "Format cells appropriately"
          ]
        }
      ]
    },
    {
      id: "ws-y12-s2-03",
      title: "Worksheet 6: Database Concepts",
      strand: "Strand 2",
      subStrand: "Databases II",
      slosCovered: ["12ICT2.3.1.1", "12ICT2.3.1.8", "12ICT2.3.2.1"],
      instructions: "Understand database structures and design.",
      activities: [
        {
          type: "comparison",
          title: "Activity 1: Compare Database Types",
          instruction: "Complete a comparison table for Network, Relational, and Hierarchical databases",
          criteria: ["Structure", "Flexibility", "Complexity", "Common Use"]
        },
        {
          type: "design",
          title: "Activity 2: Design a Database",
          scenario: "Design a database for a video rental store",
          requirements: [
            "Identify at least 3 tables needed",
            "List fields for each table",
            "Identify primary keys",
            "Show relationships between tables"
          ]
        }
      ]
    }
  ],
  strand3: [
    {
      id: "ws-y12-s3-01",
      title: "Worksheet 7: Digital Image Basics",
      strand: "Strand 3",
      subStrand: "Digital Image Processing II",
      slosCovered: ["12ICT3.3.1.1", "12ICT3.3.2.1"],
      instructions: "Learn about image file formats and editing.",
      activities: [
        {
          type: "table_completion",
          title: "Activity 1: Image Format Comparison",
          headers: ["Format", "Full Name", "Best Use", "Supports Transparency"],
          rows: [
            ["JPEG", "___", "___", "___"],
            ["PNG", "___", "___", "___"],
            ["GIF", "___", "___", "___"],
            ["BMP", "___", "___", "___"]
          ]
        },
        {
          type: "practical",
          title: "Activity 2: Image Editing Practice",
          tasks: [
            "Open an image in GIMP or Photoshop",
            "Resize the image to 800x600 pixels",
            "Adjust brightness and contrast",
            "Crop the image to focus on main subject",
            "Save in two different formats"
          ]
        }
      ]
    }
  ],
  strand4: [
    {
      id: "ws-y12-s4-01",
      title: "Worksheet 8: Networking Basics",
      strand: "Strand 4",
      subStrand: "Networks II",
      slosCovered: ["12ICT4.1.1.1", "12ICT4.1.1.4", "12ICT4.1.1.7"],
      instructions: "Understand basic networking concepts.",
      activities: [
        {
          type: "definition",
          title: "Activity 1: Define Networking Terms",
          terms: ["TCP/IP", "DNS", "IP Address", "Router", "Switch", "Firewall", "Bandwidth"]
        },
        {
          type: "diagram",
          title: "Activity 2: Network Diagram",
          description: "Draw a simple network diagram showing: 3 computers, 1 switch, 1 router, and internet connection. Label all components."
        },
        {
          type: "short_answer",
          title: "Activity 3: Explain Network Concepts",
          questions: [
            "What is the difference between a router and a switch?",
            "How does DNS help users access websites?",
            "Why is TCP/IP important for internet communication?"
          ]
        }
      ]
    },
    {
      id: "ws-y12-s4-02",
      title: "Worksheet 9: Internet Security",
      strand: "Strand 4",
      subStrand: "The Internet II: Security and Safety",
      slosCovered: ["12ICT4.3.1.2", "12ICT4.3.2.2", "12ICT4.3.1.5", "12ICT4.3.2.4"],
      instructions: "Learn about cybersecurity threats and protection.",
      activities: [
        {
          type: "matching",
          title: "Activity 1: Match Threats to Descriptions",
          pairs: [
            { threat: "Phishing", description: "Fake emails/websites to steal information" },
            { threat: "Malware", description: "Malicious software that harms computers" },
            { threat: "Ransomware", description: "Encrypts files and demands payment" },
            { threat: "DDoS Attack", description: "Overwhelms servers with traffic" },
            { threat: "Identity Theft", description: "Stealing personal information" }
          ]
        },
        {
          type: "list",
          title: "Activity 2: Cybersecurity Best Practices",
          instruction: "List 10 cybersecurity practices everyone should follow"
        },
        {
          type: "case_study",
          title: "Activity 3: Security Case Study",
          scenario: "A company's employee clicks on a phishing email and their network is compromised.",
          questions: [
            "What could have prevented this attack?",
            "What steps should the company take immediately?",
            "How can they prevent future attacks?"
          ]
        }
      ]
    }
  ]
};

// ============================================
// STUDENT RESOURCES
// ============================================
export const year12ICTResources = {
  textbooks: [
    { title: "Year 12 ICT Textbook - Vanuatu Edition", type: "Primary Textbook" },
    { title: "Computer Hardware Essentials", type: "Reference" },
    { title: "Microsoft Office 365 Guide", type: "Software Guide" },
    { title: "Introduction to Python Programming", type: "Programming Guide" }
  ],
  onlineResources: [
    { name: "W3Schools", url: "https://www.w3schools.com", description: "Web development and programming tutorials" },
    { name: "Khan Academy Computing", url: "https://www.khanacademy.org/computing", description: "Free computing courses" },
    { name: "GCFGlobal", url: "https://edu.gcfglobal.org/en/topics/technology/", description: "Free technology tutorials" },
    { name: "Codecademy", url: "https://www.codecademy.com", description: "Interactive programming lessons" }
  ],
  software: [
    { name: "Microsoft Office 365", purpose: "Word processing, spreadsheets, presentations" },
    { name: "GIMP", purpose: "Free image editing software" },
    { name: "Python IDLE", purpose: "Python programming environment" },
    { name: "LibreOffice Base", purpose: "Free database software" },
    { name: "WordPress", purpose: "Content Management System for web development" }
  ],
  videos: [
    { title: "How Computers Work", topic: "Hardware", duration: "15 min" },
    { title: "Building Your First PC", topic: "Hardware Assembly", duration: "30 min" },
    { title: "Excel Formulas Tutorial", topic: "Spreadsheets", duration: "20 min" },
    { title: "Introduction to Databases", topic: "Databases", duration: "25 min" },
    { title: "Network Basics Explained", topic: "Networking", duration: "20 min" },
    { title: "Cybersecurity Fundamentals", topic: "Security", duration: "25 min" }
  ]
};

export default year12ICTCurriculum;
